import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useDarkMode } from "../../components/Theme/useDarkMode";
import { fetchPackageDetail, fetchPackageTraces } from "./api";
import {
  LevelDot,
  Mono,
  PhasePill,
  StateBlock,
  TRACE_PHASE_LABELS,
  formatClock,
  formatDateTime,
  formatRelative,
} from "./shared";
import "./NewfiSplitEdocs.css";

const TRACE_PAGE_SIZE = 200;

const STEP_LABELS = {
  import: "Import",
  processing: "Processing",
  writeback: "Write-back",
};

function Field({ label, children }) {
  return (
    <div className="nse-field">
      <span className="nse-field__label">{label}</span>
      <span className="nse-field__value">{children}</span>
    </div>
  );
}

function StepCard({ name, step }) {
  const status = step?.status || "pending";
  return (
    <div className={`nse-step nse-step--${status}`}>
      <div className="nse-step__head">
        <span className="nse-step__name">{STEP_LABELS[name] || name}</span>
        <span className="nse-step__status">{status.replace("_", " ")}</span>
      </div>
      <div className="nse-step__meta">
        {step?.attempts ? <span>{step.attempts} deferrals/failures</span> : null}
        {step?.startedAt ? (
          <span title={formatDateTime(step.startedAt)}>
            started {formatRelative(step.startedAt)}
          </span>
        ) : null}
        {step?.completedAt ? (
          <span title={formatDateTime(step.completedAt)}>
            done {formatRelative(step.completedAt)}
          </span>
        ) : null}
      </div>
      {step?.lastError && <div className="nse-step__error">{step.lastError}</div>}
    </div>
  );
}

/**
 * One entry in the package's history.
 *
 * The message is written to be read on its own — the evidence behind a decision
 * is in the sentence, not only in the payload — so the metadata stays collapsed
 * until someone actually wants it.
 */
function TraceRow({ trace }) {
  const [open, setOpen] = useState(false);
  const hasData = trace.data && Object.keys(trace.data).length > 0;

  return (
    <li className={`nse-trace nse-trace--${trace.level || "info"}`}>
      <div className="nse-trace__gutter">
        <LevelDot level={trace.level} />
      </div>
      <div className="nse-trace__body">
        <div className="nse-trace__head">
          <span className="nse-trace__time" title={formatDateTime(trace.at)}>
            {formatClock(trace.at)}
          </span>
          <span className="nse-tag">
            {TRACE_PHASE_LABELS[trace.phase] || trace.phase}
          </span>
          <Mono value={trace.code} />
          {trace.type && trace.type !== "decision" && (
            <span className="nse-tag nse-tag--type">{trace.type}</span>
          )}
        </div>
        <div className="nse-trace__message">{trace.message}</div>
        {hasData && (
          <>
            <button
              type="button"
              className="nse-trace__toggle"
              onClick={() => setOpen((value) => !value)}
            >
              {open ? "Hide details" : "Show details"}
            </button>
            {open && (
              <pre className="nse-trace__data">
                {JSON.stringify(trace.data, null, 2)}
              </pre>
            )}
          </>
        )}
      </div>
    </li>
  );
}

export default function NewfiSplitEdocDetailPage({ onReady }) {
  const [theme] = useDarkMode();
  const { recordId } = useParams();
  const location = useLocation();
  const env = useMemo(
    () => new URLSearchParams(location.search).get("env") || "prod",
    [location.search],
  );

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [traces, setTraces] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [levelFilter, setLevelFilter] = useState("");
  const [phaseFilter, setPhaseFilter] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPackageDetail(recordId, { traceLimit: TRACE_PAGE_SIZE }, env)
      .then((res) => {
        if (cancelled) {
          return;
        }
        if (!res.success) {
          throw new Error(res.reason || "Failed to load this package.");
        }
        setDetail(res);
        setTraces(Array.isArray(res.traces) ? res.traces : []);
        setCursor(res.tracesNextCursor || null);
        setHasMore(Boolean(res.tracesHasMore));
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          onReady?.();
        }
      });
    return () => {
      cancelled = true;
    };
  }, [recordId, env, onReady]);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const res = await fetchPackageTraces(
        recordId,
        { limit: TRACE_PAGE_SIZE, cursor },
        env,
      );
      if (res.success) {
        setTraces((current) => [...current, ...(res.traces || [])]);
        setCursor(res.nextCursor || null);
        setHasMore(Boolean(res.hasMore));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  }, [recordId, cursor, env]);

  // Filtered client-side: the whole trail is already here, and a round trip to
  // re-filter tens of rows would only add latency.
  const visibleTraces = useMemo(() => {
    const term = search.trim().toLowerCase();
    return traces.filter((trace) => {
      if (levelFilter && trace.level !== levelFilter) {
        return false;
      }
      if (phaseFilter && trace.phase !== phaseFilter) {
        return false;
      }
      if (!term) {
        return true;
      }
      return (
        String(trace.message || "").toLowerCase().includes(term) ||
        String(trace.code || "").toLowerCase().includes(term)
      );
    });
  }, [traces, levelFilter, phaseFilter, search]);

  const pkg = detail?.package;
  const push = detail?.push;
  const errorCount = traces.filter((trace) => trace.level === "error").length;

  return (
    <div className="nse-page" data-theme={theme}>
      <div className="nse-breadcrumb">
        <Link to={`/newfi-split-edocs?env=${env}`}>← All packages</Link>
        <span className="nse-env-badge">{env}</span>
      </div>

      <StateBlock
        loading={loading}
        error={error}
        empty={!detail}
        emptyText="Package not found."
      >
        {detail && (
          <>
            <div className="nse-header">
              <div>
                <h1 className="nse-title">
                  {pkg.loanNumber || "Unknown loan"}
                  <PhasePill phase={pkg.phase} />
                </h1>
                <p className="nse-subtitle">
                  {pkg.docType || "Unknown package type"} ·{" "}
                  <Mono value={pkg.meridianLinkDocumentId} />
                </p>
              </div>
            </div>

            <section className="nse-card">
              <div className="nse-fields">
                <Field label="Organization">
                  <Mono value={pkg.organizationId} />
                </Field>
                <Field label="Addy loan">
                  <Mono value={pkg.addyLoanId} />
                </Field>
                <Field label="Addy document">
                  <Mono value={pkg.addyDocumentId} />
                </Field>
                <Field label="Channel">{pkg.packageChannel || "—"}</Field>
                <Field label="Package kind">{pkg.packageKind || "—"}</Field>
                <Field label="Splits">{pkg.splitCount ?? "—"}</Field>
                <Field label="Push outcome">
                  {pkg.pushOutcome ? (
                    <span
                      className={`nse-tag nse-tag--${
                        pkg.pushOutcome.startsWith("applied") ? "ok" : "warn"
                      }`}
                    >
                      {pkg.pushOutcome}
                    </span>
                  ) : (
                    "Not pushed"
                  )}
                </Field>
                <Field label="Uploaded / failed">
                  {pkg.uploadedSplitCount ?? "—"} / {pkg.failedSplitCount ?? "—"}
                </Field>
                <Field label="Origin">
                  {pkg.origin === "writeback"
                    ? "Pushed without pipeline ingestion"
                    : "Detected by the poller"}
                </Field>
                <Field label="Detected">
                  <span title={formatDateTime(pkg.createdAt)}>
                    {formatRelative(pkg.createdAt)}
                  </span>
                </Field>
                <Field label="Last updated">
                  <span title={formatDateTime(pkg.updatedAt)}>
                    {formatRelative(pkg.updatedAt)}
                  </span>
                </Field>
                {detail.claim?.nextCheckAt && (
                  <Field label="Next scheduler check">
                    <span title={formatDateTime(detail.claim.nextCheckAt)}>
                      {formatRelative(detail.claim.nextCheckAt)}
                    </span>
                  </Field>
                )}
              </div>

              {(pkg.outOfScopeReason || pkg.abandonedReason) && (
                <div className="nse-notice nse-notice--warn">
                  {pkg.outOfScopeReason || pkg.abandonedReason}
                </div>
              )}
            </section>

            <section className="nse-steps">
              {["import", "processing", "writeback"].map((name) => (
                <StepCard key={name} name={name} step={detail.steps?.[name]} />
              ))}
            </section>

            {push && (
              <section className="nse-card">
                <h2 className="nse-card__title">Push ledger</h2>
                <div className="nse-fields">
                  <Field label="Status">{push.status || "—"}</Field>
                  <Field label="Started">
                    {push.claimedAt ? formatDateTime(push.claimedAt) : "—"}
                  </Field>
                  <Field label="Pre-write stamped">
                    {push.startStampedAt
                      ? formatDateTime(push.startStampedAt)
                      : "No"}
                  </Field>
                  <Field label="Original obsoleted">
                    {push.obsoletedAt ? formatDateTime(push.obsoletedAt) : "No"}
                  </Field>
                  {push.obsoleteFailureReason && (
                    <Field label="Obsolete failure">
                      {push.obsoleteFailureReason}
                    </Field>
                  )}
                  {push.failureReason && (
                    <Field label="Failure">{push.failureReason}</Field>
                  )}
                </div>
              </section>
            )}

            <section className="nse-card">
              <div className="nse-card__head">
                <h2 className="nse-card__title">
                  History
                  <span className="nse-count">
                    {traces.length} event{traces.length === 1 ? "" : "s"}
                    {errorCount > 0 && (
                      <span className="nse-error-count">{errorCount} err</span>
                    )}
                  </span>
                </h2>
                <div className="nse-controls">
                  <select
                    className="nse-select"
                    value={phaseFilter}
                    onChange={(event) => setPhaseFilter(event.target.value)}
                  >
                    <option value="">All stages</option>
                    {Object.entries(TRACE_PHASE_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="nse-select"
                    value={levelFilter}
                    onChange={(event) => setLevelFilter(event.target.value)}
                  >
                    <option value="">All levels</option>
                    <option value="info">info</option>
                    <option value="warn">warn</option>
                    <option value="error">error</option>
                  </select>
                  <input
                    className="nse-input"
                    placeholder="Search history…"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </div>
              </div>

              {visibleTraces.length === 0 ? (
                <div className="nse-state nse-muted">
                  {traces.length === 0
                    ? "No traces recorded for this package yet. Packages processed before the audit trail shipped have none."
                    : "No events match these filters."}
                </div>
              ) : (
                <ol className="nse-timeline">
                  {visibleTraces.map((trace) => (
                    <TraceRow key={trace.id} trace={trace} />
                  ))}
                </ol>
              )}

              {hasMore && (
                <div className="nse-load-more">
                  <button
                    type="button"
                    className="nse-btn"
                    disabled={loadingMore}
                    onClick={loadMore}
                  >
                    {loadingMore ? "Loading…" : "Load older events"}
                  </button>
                </div>
              )}
            </section>
          </>
        )}
      </StateBlock>
    </div>
  );
}
